/**
 * v3.18-1263 PAYMENT-007B — Multi-Card Authorization Workflow.
 *
 * Renders every authorized credit card on file for a client (unlimited,
 * newest first). Each row supports:
 *   • Reveal Card — requires a mandatory reason, then unmasks the PAN/CVV
 *     for 60 seconds. Automatically re-masks on:
 *       – 60s countdown
 *       – Tab switch / window blur / minimize / visibility hidden
 *       – Route change / page refresh
 *       – Clicking outside the revealed row
 *       – Manual "Hide" click
 *     Every fresh reveal starts a NEW 60s timer AND generates its own
 *     immutable audit + Activity Timeline + Finance History rows.
 *   • Delete Card Info — purges sensitive fields.
 *   • Mark Entered in Bill — legacy manual reconciliation.
 *   • Charge — Terminal 1 / Terminal 2 — routes to the neutral Payment
 *     Engine using this authorization (no PAN re-entry).
 *
 * Visible ONLY to admin/director roles.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CreditCard, Trash2, CheckCircle2, Loader2, ShieldAlert, Zap, Eye, EyeOff, Plus,
  Undo2, XCircle, Receipt, RefreshCw, Landmark, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import CreditCardFeeCalculator from './CreditCardFeeCalculator';
import ChargeCardDialog from './ChargeCardDialog';
import RevealCardReasonDialog from './RevealCardReasonDialog';
import EnterNewCreditCardDialog from './EnterNewCreditCardDialog';
import RecordOfflinePaymentDialog from './RecordOfflinePaymentDialog';
import ReversalHistoryBlock from './ReversalHistoryBlock';  // v3.18-1298

const ADMIN_DIRECTOR_ROLES = new Set([
  'ptc_admin', 'admin', 'ptc_director',
  'company_admin', 'company_director',
]);

const REVEAL_WINDOW_SEC = 300;  // v3.18-1293 — 5 min reveal window per operator directive

// v3.18-1293 — Format seconds → M:SS countdown for the reveal chip.
const fmtCountdown = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const formatCurrency = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatLocalDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const useExpiryCountdown = (expiresAtIso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAtIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [expiresAtIso]);
  if (!expiresAtIso) return null;
  const expMs = new Date(expiresAtIso).getTime();
  const diffMs = Math.max(0, expMs - now);
  const minutes = Math.ceil(diffMs / 60_000);
  if (minutes > 60) {
    return { value: Math.round(minutes / 60), unit: 'hours' };
  }
  return { value: minutes, unit: 'min' };
};

// v3.18-1293 — Payment-status theming for the banner + row border.
const STATUS_META = {
  saved:      { bar: 'bg-amber-100 border-amber-300 text-amber-900',   dot: '🟡', label: 'Card Saved — No transaction attempted' },
  processing: { bar: 'bg-blue-100  border-blue-300  text-blue-900',    dot: '🔵', label: 'Processing…' },
  approved:   { bar: 'bg-emerald-100 border-emerald-300 text-emerald-900', dot: '🟢', label: 'Approved' },
  declined:   { bar: 'bg-rose-100  border-rose-300  text-rose-900',    dot: '🔴', label: 'Declined' },
  refunded:   { bar: 'bg-purple-100 border-purple-300 text-purple-900', dot: '🟣', label: 'Refunded' },
  voided:     { bar: 'bg-slate-200 border-slate-400 text-slate-800',   dot: '⚫', label: 'Voided' },
  offline:    { bar: 'bg-indigo-100 border-indigo-300 text-indigo-900', dot: '🟦', label: 'Processed Offline (outside the Hub)' },
};

// ─── Single row for one saved authorization ───────────────────────────────
const AuthorizationRow = ({
  record, clientId, busy, onDelete, onOfflinePayment, onCharge,
  onRefund, onVoid, onChargeAgain, revealed, onRevealRequest, onHide, remainingSec,
  reversalRefreshKey,
}) => {
  const rowRef = useRef(null);
  const expiryInfo = useExpiryCountdown(record?.expires_at);

  // Auto re-mask on outside click while this row is revealed.
  useEffect(() => {
    if (!revealed) return undefined;
    const handler = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) {
        onHide();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [revealed, onHide]);

  const displayNumber = revealed?.card_number
    ? revealed.card_number
    : (record.card_masked || (record.last4 ? `•••• •••• •••• ${record.last4}` : '—'));
  const displayCvv = revealed?.cvv || (record.card_number_purged ? '(purged)' : '•••');
  const displayExp = revealed?.exp || record.exp_masked || record.exp || '••/••';
  const displayZip = revealed?.billing_zip || record.billing_zip_masked || record.billing_zip || '—';

  return (
    <div
      ref={rowRef}
      className={`rounded-md border p-3 space-y-2 transition-colors ${
        revealed ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-300' : 'bg-white border-rose-200'
      }`}
      data-testid={`cc-auth-row-${record.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Confirmation #
          </div>
          <div className="font-mono font-semibold text-slate-800 text-xs" data-testid={`cc-auth-confirmation-${record.id}`}>
            {record.confirmation_number || '—'}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={`text-[10px] ${
            record.status === 'Linked' ? 'bg-emerald-100 text-emerald-800'
            : record.status === 'Unlinked' ? 'bg-amber-100 text-amber-800'
            : 'bg-slate-100 text-slate-700'
          }`}>
            {record.status || 'Linked'}
          </Badge>
          {expiryInfo ? (
            <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300">
              <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />
              Purges in {expiryInfo.value} {expiryInfo.unit}
            </Badge>
          ) : null}
        </div>
      </div>

      {/* v3.18-1293 PAYMENT-STATUS — prominent status banner so the
          operator immediately sees the processing state of this card. */}
      {(() => {
        const status = record.payment_status || 'saved';
        const meta   = STATUS_META[status] || STATUS_META.saved;
        return (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${meta.bar}`}
            data-testid={`cc-auth-payment-status-banner-${record.id}`}
          >
            <div className="font-semibold flex items-center gap-1.5">
              <span className="text-sm" aria-hidden>{meta.dot}</span>
              <span data-testid={`cc-auth-payment-status-${record.id}`}>{meta.label}</span>
            </div>
            {status !== 'saved' && (
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1.5 text-[11px]">
                {record.payment_amount != null && (
                  <div><span className="opacity-70">Amount:</span> <span className="font-medium">{formatCurrency(record.payment_amount)}</span></div>
                )}
                {record.payment_terminal && (
                  <div><span className="opacity-70">Terminal:</span> <span className="font-medium">{record.payment_terminal}</span></div>
                )}
                {record.payment_approval_code && (
                  <div><span className="opacity-70">Approval Code:</span> <span className="font-mono">{record.payment_approval_code}</span></div>
                )}
                {record.payment_transaction_id && (
                  <div className="truncate" title={record.payment_transaction_id}><span className="opacity-70">Transaction ID:</span> <span className="font-mono">{record.payment_transaction_id}</span></div>
                )}
                {record.payment_processed_by && (
                  <div><span className="opacity-70">Processed By:</span> <span className="font-medium">{record.payment_processed_by}</span></div>
                )}
                {record.payment_processed_at && (
                  <div><span className="opacity-70">Processed:</span> <span className="font-medium">{formatLocalDateTime(record.payment_processed_at)}</span></div>
                )}
                {status === 'declined' && record.payment_decline_reason && (
                  <div className="col-span-2"><span className="opacity-70">Reason:</span> <span className="font-medium">{record.payment_decline_reason}</span></div>
                )}
                {status === 'offline' && record.payment_notes && (
                  <div className="col-span-2"><span className="opacity-70">Notes:</span> <span className="italic">{record.payment_notes}</span></div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* v3.18-1298 P5 — Complete reversal ledger (original + refunds
          + voids + remaining balance) inline on the card. */}
      <ReversalHistoryBlock
        authorizationId={record.id}
        refreshKey={reversalRefreshKey}
      />

      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <div className="col-span-2">
          <span className="text-slate-500">Card Number{record.brand ? ` · ${record.brand}` : ''}</span>
          <div
            className="font-mono font-semibold text-slate-800 text-sm tracking-wider flex items-center gap-2"
            data-testid={`cc-auth-card-number-${record.id}`}
          >
            <span className={revealed ? 'text-amber-900' : ''}>{displayNumber}</span>
            {revealed ? (
              <span
                className="text-[10px] font-mono bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded"
                data-testid={`cc-auth-reveal-timer-${record.id}`}
                title="Card auto-hides when the countdown reaches 0:00"
              >
                Card Visible · Time Remaining: {fmtCountdown(remainingSec)}
              </span>
            ) : null}
          </div>
        </div>
        <div>
          <span className="text-slate-500">Exp</span>
          <div className="font-mono font-medium text-slate-800" data-testid={`cc-auth-exp-${record.id}`}>
            {displayExp}
          </div>
        </div>
        <div>
          <span className="text-slate-500">CVV</span>
          <div className="font-mono font-medium text-slate-800" data-testid={`cc-auth-cvv-${record.id}`}>
            {displayCvv}
          </div>
        </div>
        <div className="col-span-2">
          <span className="text-slate-500">Name</span>
          <div className="font-medium text-slate-800" data-testid={`cc-auth-name-${record.id}`}>
            {record.name_on_card || '—'}
          </div>
        </div>
        <div>
          <span className="text-slate-500">Amount</span>
          <div className="font-semibold text-slate-800">{formatCurrency(record.amount)}</div>
        </div>
        <div>
          <span className="text-slate-500">Billing ZIP</span>
          <div className="font-mono text-slate-800">{displayZip}</div>
        </div>
        <div className="col-span-2">
          <span className="text-slate-500">Received</span>
          <div className="text-slate-700">{formatLocalDateTime(record.created_at)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 pt-1">
        {(() => {
          const status = record.payment_status || 'saved';
          const isTerminal = ['approved', 'refunded', 'voided', 'declined', 'offline'].includes(status);
          const canRevealPan = !!record.can_reveal && !record.card_number_purged;
          // v3.18-1293 PAYMENT-STATUS — button visibility rules:
          //   • saved / declined:  Reveal + T1 + T2 + Offline + Delete
          //   • processing:        (disabled) Processing…
          //   • approved:          View Receipt + Refund + Void + Charge Again (business rule)
          //   • refunded / voided: View Receipt + (Charge Again if permitted)
          //   • offline:           View Notes + (no re-charge — record is final)
          if (status === 'processing') {
            return (
              <Button size="sm" disabled className="h-7 text-xs" data-testid={`cc-auth-processing-${record.id}`}>
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing…
              </Button>
            );
          }
          if (status === 'approved' || status === 'refunded' || status === 'voided') {
            return (
              <>
                {record.payment_receipt_url && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => window.open(record.payment_receipt_url, '_blank', 'noopener')}
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 h-7 text-xs"
                    data-testid={`cc-auth-view-receipt-btn-${record.id}`}
                  >
                    <Receipt className="h-3 w-3 mr-1" /> View Receipt
                  </Button>
                )}
                {status === 'approved' && (
                  <>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => onRefund?.()} disabled={busy}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50 h-7 text-xs"
                      data-testid={`cc-auth-refund-btn-${record.id}`}
                    >
                      <Undo2 className="h-3 w-3 mr-1" /> Refund
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => onVoid?.()} disabled={busy}
                      className="border-slate-400 text-slate-700 hover:bg-slate-100 h-7 text-xs"
                      data-testid={`cc-auth-void-btn-${record.id}`}
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Void
                    </Button>
                  </>
                )}
                {canRevealPan && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => onChargeAgain?.()} disabled={busy}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 text-xs"
                    data-testid={`cc-auth-charge-again-btn-${record.id}`}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Charge Again
                  </Button>
                )}
              </>
            );
          }
          if (status === 'offline') {
            return (
              <Badge className="bg-indigo-100 text-indigo-800 text-[10px]" data-testid={`cc-auth-offline-note-${record.id}`}>
                <Landmark className="h-3 w-3 mr-1" /> Processed outside the Hub — record is final
              </Badge>
            );
          }
          // status === 'saved' or 'declined' → pre-processing controls
          return (
            <>
              {revealed ? (
                <Button
                  size="sm" onClick={onHide}
                  className="bg-amber-700 hover:bg-amber-800 text-white h-7 text-xs"
                  data-testid={`cc-auth-hide-btn-${record.id}`}
                >
                  <EyeOff className="h-3 w-3 mr-1" /> Hide Card
                </Button>
              ) : (
                <Button
                  size="sm" variant="outline"
                  disabled={busy || !record.can_reveal}
                  onClick={onRevealRequest}
                  className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7 text-xs"
                  data-testid={`cc-auth-reveal-btn-${record.id}`}
                >
                  <Eye className="h-3 w-3 mr-1" /> Reveal Card
                </Button>
              )}
              <Button
                size="sm" onClick={() => onCharge('ptc_terminal_1')} disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white h-7 text-xs"
                data-testid={`cc-auth-charge-t1-btn-${record.id}`}
              >
                <Zap className="h-3 w-3 mr-1" /> Charge T1
              </Button>
              <Button
                size="sm" onClick={() => onCharge('ptc_terminal_2')} disabled={busy}
                className="bg-slate-700 hover:bg-slate-600 text-white h-7 text-xs"
                data-testid={`cc-auth-charge-t2-btn-${record.id}`}
              >
                <Zap className="h-3 w-3 mr-1" /> Charge T2
              </Button>
              <Button
                size="sm" variant="outline" onClick={onOfflinePayment} disabled={busy}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-7 text-xs"
                data-testid={`cc-auth-record-offline-btn-${record.id}`}
              >
                <Landmark className="h-3 w-3 mr-1" /> Record Offline Payment
              </Button>
              {!isTerminal && (
                <Button
                  size="sm" variant="outline" onClick={onDelete} disabled={busy}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 h-7 text-xs"
                  data-testid={`cc-auth-delete-btn-${record.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export const CreditCardAuthorizationBlock = ({ clientId, userRole, clientEmail, clientPhone, clientName }) => {
  const canSee = ADMIN_DIRECTOR_ROLES.has((userRole || '').toLowerCase());

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // v3.18-1298 — bumped after every successful refund/void so each
  // ReversalHistoryBlock re-fetches its ledger.
  const [reversalRefreshKey, setReversalRefreshKey] = useState(0);

  // Enter new card dialog
  const [enterOpen, setEnterOpen] = useState(false);

  // Reveal state
  const [reasonDialogFor, setReasonDialogFor] = useState(null);   // record.id awaiting reason
  const [revealSubmitting, setRevealSubmitting] = useState(false);
  const [revealedById, setRevealedById] = useState({});           // { [rec.id]: { card_number, cvv, exp, ... } }
  const [remainingById, setRemainingById] = useState({});         // { [rec.id]: seconds }
  const timersRef = useRef({});                                   // per-card interval handles

  // Charge dialog state
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargePreferred, setChargePreferred] = useState(null);
  const [chargeAuthorizationId, setChargeAuthorizationId] = useState(null);

  // v3.18-1293 — Record Offline Payment dialog target
  const [offlinePaymentFor, setOfflinePaymentFor] = useState(null);

  const clearTimer = useCallback((recordId) => {
    if (timersRef.current[recordId]) {
      clearInterval(timersRef.current[recordId]);
      delete timersRef.current[recordId];
    }
  }, []);

  const hideCard = useCallback((recordId) => {
    setRevealedById((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    setRemainingById((prev) => {
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    clearTimer(recordId);
  }, [clearTimer]);

  const hideAllCards = useCallback(() => {
    Object.keys(timersRef.current).forEach((id) => clearInterval(timersRef.current[id]));
    timersRef.current = {};
    setRevealedById({});
    setRemainingById({});
  }, []);

  // Global re-mask triggers — visibility change, window blur, unload/navigation
  useEffect(() => {
    if (Object.keys(revealedById).length === 0) return undefined;
    const onVisibility = () => { if (document.visibilityState === 'hidden') hideAllCards(); };
    const onBlur = () => hideAllCards();
    const onBeforeUnload = () => hideAllCards();
    window.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
    };
  }, [revealedById, hideAllCards]);

  // On unmount (route change / logout), guarantee all revealed cards are dropped.
  useEffect(() => () => {
    Object.keys(timersRef.current).forEach((id) => clearInterval(timersRef.current[id]));
    timersRef.current = {};
  }, []);

  const fetchList = useCallback(async () => {
    if (!canSee || !clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/credit-card-auth/list`);
      setRecords(Array.isArray(data?.records) ? data.records : []);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [canSee, clientId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  if (!canSee) return null;

  const handleDelete = async (rec) => {
    if (!rec?.id) return;
    if (!window.confirm(`Delete card •••• ${rec.last4 || '----'}? This cannot be undone.`)) return;
    setBusy(true);
    hideCard(rec.id);
    try {
      await api.delete(`/clients/${clientId}/credit-card-auth/${rec.id}`);
      toast.success('Credit card data deleted.');
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete card data.');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkEntered = async (rec) => {
    // v3.18-1293 — retained for reference; UI now routes to
    // handleOpenOfflinePayment (see below) which captures amount /
    // processor / auth-code / notes.  Legacy caller kept in case any
    // external hook still calls the /mark-entered endpoint directly.
    if (!rec?.id) return;
    setBusy(true);
    hideCard(rec.id);
    try {
      await api.post(`/clients/${clientId}/credit-card-auth/${rec.id}/mark-entered`);
      toast.success('Marked as entered. Card data wiped.');
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status.');
    } finally {
      setBusy(false);
    }
  };

  // v3.18-1293 — Record Offline Payment: opens the new capture dialog.
  const handleOpenOfflinePayment = (rec) => {
    hideCard(rec.id);
    setOfflinePaymentFor(rec);
  };

  // v3.18-1297 PAYMENT-007Q — Refund / Void now execute real
  // reversals against the neutral Payment Engine. Every reversal
  // writes an immutable payment_transactions row, updates the
  // Activity Timeline, Finance History, Payment Center, Reports,
  // Settlement, and system_audit via the same cascade used by
  // charge_card().
  const runReversal = async (rec, type) => {
    const label = type === 'refund' ? 'Refund' : 'Void';
    const reason = window.prompt(
      `${label} reason (required for audit trail):`,
      type === 'refund' ? 'Client requested refund' : 'Same-day void',
    );
    if (reason === null) return;               // cancelled
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (!window.confirm(`${label} the charge for authorization ${rec.confirmation_number || rec.id}? This cannot be undone.`)) return;

    let tx;
    try {
      const { data } = await api.get(`/payments/engine/authorizations/${rec.id}/latest-approved`);
      tx = data;
    } catch (err) {
      toast.error(err?.response?.status === 404
        ? 'No approved payment found on this authorization.'
        : 'Failed to look up the underlying transaction.');
      return;
    }

    try {
      const { data } = await api.post(
        `/payments/engine/transactions/${tx.id}/${type}`,
        { reason: reason.trim() },
      );
      toast.success(`${label} successful — audit + timeline updated`);
      // Refresh the authorization list so the row's status flips.
      await fetchList();
      setReversalRefreshKey((k) => k + 1);
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || `${label} failed`);
      toast.error(message);
    }
  };
  const handleRefund = (rec) => runReversal(rec, 'refund');
  const handleVoid   = (rec) => runReversal(rec, 'void');
  const handleChargeAgain = (rec) => {
    setChargePreferred(null);
    setChargeAuthorizationId(rec.id);
    setChargeOpen(true);
  };

  const handleCharge = (rec, terminalId) => {
    setChargePreferred(terminalId);
    setChargeAuthorizationId(rec.id);
    setChargeOpen(true);
  };

  const startRevealCountdown = (recordId) => {
    clearTimer(recordId);
    setRemainingById((prev) => ({ ...prev, [recordId]: REVEAL_WINDOW_SEC }));
    timersRef.current[recordId] = setInterval(() => {
      setRemainingById((prev) => {
        const cur = prev[recordId];
        if (cur === undefined) return prev;
        if (cur <= 1) {
          clearTimer(recordId);
          // Re-mask via a microtask so React batches consistently.
          setTimeout(() => hideCard(recordId), 0);
          return { ...prev, [recordId]: 0 };
        }
        return { ...prev, [recordId]: cur - 1 };
      });
    }, 1000);
  };

  const handleConfirmReveal = async (reason) => {
    if (!reasonDialogFor || !reason?.trim()) return;
    setRevealSubmitting(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/credit-card-auth/${reasonDialogFor}/reveal`,
        { reason: reason.trim() },
      );
      setRevealedById((prev) => ({
        ...prev,
        [reasonDialogFor]: {
          card_number: data.card_number,
          cvv: data.cvv,
          exp: data.exp,
          name_on_card: data.name_on_card,
          billing_zip: data.billing_zip,
          revealed_at: data.reveal_timestamp,
        },
      }));
      startRevealCountdown(reasonDialogFor);
      setReasonDialogFor(null);
      toast.success('Card revealed — auto-remask in 60 seconds. Audit recorded.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reveal card.');
    } finally {
      setRevealSubmitting(false);
    }
  };

  const revealTargetRecord = reasonDialogFor ? records.find((r) => r.id === reasonDialogFor) : null;

  return (
    <Card
      className="border-rose-200 bg-rose-50/30 mt-3"
      data-testid="credit-card-auth-block"
    >
      <CardHeader className="py-2 px-3">
        <CardTitle className="text-sm font-semibold flex items-center justify-between text-rose-800">
          <span className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Credit Card Authorizations
            <Badge variant="outline" className="text-slate-600 text-[10px]" data-testid="cc-auth-count-badge">
              {records.length}
            </Badge>
          </span>
          <div className="flex items-center gap-1.5">
            {records.length === 0 ? (
              <Badge variant="outline" className="text-slate-500 text-[10px]">
                No card on file
              </Badge>
            ) : (
              <Badge className="bg-rose-100 text-rose-700 text-[10px]">
                <ShieldAlert className="h-3 w-3 mr-1" />
                Audited access
              </Badge>
            )}
            <Button
              size="sm"
              onClick={() => setEnterOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white h-7 text-xs"
              data-testid="cc-auth-enter-new-btn"
            >
              <Plus className="h-3 w-3 mr-1" />
              Enter New Credit Card
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-2">
        <CreditCardFeeCalculator defaultTarget={records[0]?.amount || ''} />

        {loading && (
          <div className="text-xs text-slate-500 py-1.5 flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        )}
        {!loading && records.length === 0 && (
          <p className="text-xs text-slate-500 py-1.5">
            No credit card data has been received for this client.
          </p>
        )}
        {!loading && records.length > 0 && (
          <div className="space-y-2" data-testid="cc-auth-records-list">
            {records.map((rec) => (
              <AuthorizationRow
                key={rec.id}
                record={rec}
                clientId={clientId}
                busy={busy}
                revealed={revealedById[rec.id] || null}
                remainingSec={remainingById[rec.id] ?? REVEAL_WINDOW_SEC}
                onRevealRequest={() => setReasonDialogFor(rec.id)}
                onHide={() => hideCard(rec.id)}
                onDelete={() => handleDelete(rec)}
                onOfflinePayment={() => handleOpenOfflinePayment(rec)}
                onCharge={(terminalId) => handleCharge(rec, terminalId)}
                onRefund={() => handleRefund(rec)}
                onVoid={() => handleVoid(rec)}
                onChargeAgain={() => handleChargeAgain(rec)}
                reversalRefreshKey={reversalRefreshKey}
              />
            ))}
          </div>
        )}

        <p className="text-[10px] text-rose-600 leading-snug pt-1">
          <ShieldAlert className="inline h-2.5 w-2.5 mr-0.5" />
          Sensitive data — auto-purges after 12 hours. Reveals are audited immutably.
        </p>
      </CardContent>

      <ChargeCardDialog
        open={chargeOpen}
        onOpenChange={(v) => { if (!v) { setChargeAuthorizationId(null); } setChargeOpen(v); }}
        clientId={clientId}
        preferredTerminalId={chargePreferred}
        creditCardAuthorizationId={chargeAuthorizationId}
      />

      {/* v3.18-1293 — Record Offline Payment (replaces "Mark Entered") */}
      <RecordOfflinePaymentDialog
        open={!!offlinePaymentFor}
        onOpenChange={(v) => { if (!v) setOfflinePaymentFor(null); }}
        clientId={clientId}
        record={offlinePaymentFor}
        onRecorded={async () => { setOfflinePaymentFor(null); await fetchList(); }}
      />

      <RevealCardReasonDialog
        open={!!reasonDialogFor}
        onOpenChange={(v) => { if (!v) setReasonDialogFor(null); }}
        onConfirm={handleConfirmReveal}
        submitting={revealSubmitting}
        cardLast4={revealTargetRecord?.last4}
      />

      <EnterNewCreditCardDialog
        open={enterOpen}
        onOpenChange={setEnterOpen}
        clientId={clientId}
        clientEmail={clientEmail}
        clientPhone={clientPhone}
        clientName={clientName}
        onSaved={fetchList}
      />
    </Card>
  );
};

export default CreditCardAuthorizationBlock;
